import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { previewMergeRequests, commitMergeRequests } from "../../api/requestsBulkApi";
import BulkActionConfirmStep from "./BulkActionConfirmStep";
import BulkFormImpactHint from "./BulkFormImpactHint";
import { formatBulkIdPreview } from "./bulkActionSafety";

export default function MergeRequestsDialog({ open, selectedIds = [], onClose, onMerged }) {
  const ids = useMemo(
    () => [...new Set(selectedIds.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0))],
    [selectedIds]
  );

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [clientId, setClientId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [originId, setOriginId] = useState("");
  const [step, setStep] = useState("form");

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPreview(null);
    setClientId("");
    setTypeId("");
    setOriginId("");
    setStep("form");

    if (ids.length < 2) {
      setError("Selecione pelo menos 2 solicitações para unificar.");
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await previewMergeRequests(ids);
        if (cancelled) return;
        setPreview(data);
        setClientId(String(data?.clients?.[0]?.id ?? ""));
        setTypeId(String(data?.types?.[0]?.id ?? ""));
        setOriginId(String(data?.origins?.[0]?.id ?? ""));
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || "Erro ao carregar dados para unificação.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, ids]);

  const clients = preview?.clients || [];
  const types = preview?.types || [];
  const origins = preview?.origins || [];

  const handleClose = () => {
    if (submitting) return;
    setStep("form");
    onClose?.();
  };

  const selectedClientName = clients.find((c) => String(c.id) === String(clientId))?.nome;
  const selectedTypeName = types.find((t) => String(t.id) === String(typeId))?.nome;
  const selectedOriginName = origins.find((o) => String(o.id) === String(originId))?.nome;

  const secondaryIds = useMemo(
    () => ids.filter((id) => id !== Number(preview?.primaryLeadId)),
    [ids, preview?.primaryLeadId]
  );

  const confirmLines = useMemo(
    () => [
      { label: "Protocolo principal", value: `#${preview?.primaryLeadId ?? "—"}`, severity: "warning" },
      {
        label: "Protocolos unificados (cancelados)",
        value: formatBulkIdPreview(secondaryIds),
        severity: "error",
      },
      { label: "Cliente", value: selectedClientName || clientId || "—" },
      { label: "Tipo", value: selectedTypeName || typeId || "—" },
      { label: "Origem", value: selectedOriginName || originId || "—" },
    ],
    [preview, secondaryIds, selectedClientName, selectedTypeName, selectedOriginName, clientId, typeId, originId]
  );

  const handleGoToConfirm = () => {
    if (ids.length < 2 || !preview?.primaryLeadId) return;
    if (!clientId || !typeId || !originId) {
      setError("Preencha cliente, tipo e origem.");
      return;
    }
    setError(null);
    setStep("confirm");
  };

  const handleSubmit = async () => {
    if (ids.length < 2 || !preview?.primaryLeadId) return;
    setSubmitting(true);
    setError(null);
    try {
      await commitMergeRequests({
        ids,
        primaryLeadId: Number(preview.primaryLeadId),
        clientId: Number(clientId),
        typeId: Number(typeId),
        originId: Number(originId),
      });
      handleClose();
      onMerged?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Erro ao unificar solicitações.");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "confirm") {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Revisar unificação</DialogTitle>
        {error && (
          <Box px={3} pt={1}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        <BulkActionConfirmStep
          ids={ids}
          actionTitle="Revisar unificação"
          impactVerb="impactadas pela unificação"
          irreversible
          actionDescription="As mensagens dos protocolos secundários serão movidas para o principal. Os demais serão cancelados."
          changeLines={confirmLines}
          warnings={[
            "Os protocolos secundários serão cancelados permanentemente. As mensagens serão transferidas para o protocolo principal.",
          ]}
          confirmCheckboxLabel="Entendo que os protocolos secundários serão cancelados e que a unificação não pode ser desfeita."
          reviewCheckboxLabel="Revisei o protocolo principal e a lista de protocolos que serão cancelados."
          onBack={() => {
            setError(null);
            setStep("form");
          }}
          onConfirm={handleSubmit}
          submitting={submitting}
          confirmButtonLabel="Confirmar unificação"
          confirmButtonColor="error"
          forceTypedConfirmation
          minConfirmationLevel={1}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Unificar solicitações</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <BulkFormImpactHint
            count={ids.length}
            impactVerb="impactadas pela unificação"
            irreversible
            forceTypedConfirmation
            minConfirmationLevel={1}
          />

          {preview?.primaryLeadId && (
            <Typography variant="body2" color="text.secondary">
              Protocolo principal: #{preview.primaryLeadId}
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth size="small" disabled={submitting || clients.length === 0}>
                <InputLabel>Cliente</InputLabel>
                <Select label="Cliente" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.nome || `Cliente ${c.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={submitting || types.length === 0}>
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                  {types.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.nome || `Tipo ${t.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={submitting || origins.length === 0}>
                <InputLabel>Origem</InputLabel>
                <Select label="Origem" value={originId} onChange={(e) => setOriginId(e.target.value)}>
                  {origins.map((o) => (
                    <MenuItem key={o.id} value={String(o.id)}>
                      {o.nome || `Canal ${o.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleGoToConfirm}
          disabled={submitting || loading || ids.length < 2 || !preview}
        >
          Revisar unificação
        </Button>
      </DialogActions>
    </Dialog>
  );
}
